import ChangeVisibilityModal from '@/components/ChangeVisibilityModal';
import CommentsSection from '@/components/CommentsSection/Index';
import ContentCard from '@/components/ContentCard';
import CourseMoreOptions from '@/components/CourseMoreOptions';
import GoBackMenu from '@/components/GoBackNavbar';
import HorizontalMenu from '@/components/HorizontalMenu';
import Loader from '@/components/Loader';
import SideMenu from '@/components/SideMenu';
import StudentRating from '@/components/StudentRating';
import Constants from '@/Constants';
import { useContentLanguageChange } from '@/hooks/useContentLanguageChange';
import { AvailableTranslation } from '@/models/AvailableTranslation';
import { CourseRequest } from '@/models/CourseResquest';
import { EntityType } from '@/models/EntityTypes';
import { Lesson } from '@/models/Lesson';
import List from '@/models/List';
import { StartEnrollmentRequest } from '@/models/StartEnrollmentRequest';
import { analyticsService, FlowEvents, PlaylistEvents } from '@/services/AnalyticsService';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { openModal } from '@/store/common/actions';
import { clearCourse, getCourseStudentViewRequest, increaseCourseViewsRequest, startEnrollmentRequest } from '@/store/list/action';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import './style.scss';

const CoursePage: React.FC = () => {
    const history = useHistory();
    const courseId: number = parseInt(useParams<{ id: string }>().id);

    const dispatch = useDispatch();

    const { course, courseEnrollment, personId, loading, strings, loadSuccess, classEnrollmentId } = useSelector((state: ApplicationState) => ({
        course: state.lists.currentData.course,
        courseEnrollment: state.lists.currentData.courseEnrollment,
        classEnrollmentId: state.lists.currentData.classEnrollment?.id,
        personId: state.identity.data.person.id,
        loading: state.lists.loading,
        loadSuccess: state.lists.success,
        strings: state.locale.data,
    }));

    const [courseTranslation, setCourseTranslation] = useState<List>();

    const getCourse = () => dispatch(getCourseStudentViewRequest({ personId, courseId, moduleId: Constants.PLAYLIST_MODULE_ID } as CourseRequest));

    useContentLanguageChange({ onChange: getCourse });

    useEffect(() => {
        getCourse();
        dispatch(increaseCourseViewsRequest(courseId));
    }, [strings._LANGUAGE]);

    useEffect(() => {
        return () => {
            dispatch(clearCourse());
        };
    }, []);

    useEffect(() => {
        if (course && !course.visibility && course.ownerId !== personId) {
            history.push(`/page-not-found`);
        }

        if (course) {
            const translation: AvailableTranslation | undefined = course.availableTranslations.find((t) => t.locale === strings._LANGUAGE);
            const tmpCourse: List = { ...course };

            if (translation) {
                tmpCourse.name = translation.name;
                tmpCourse.description = translation.description;
            }

            setCourseTranslation(tmpCourse);
        }
    }, [course]);

    useEffect(() => {
        if (loadSuccess && course && course.ownerId !== personId && !courseEnrollment?.id) {
            dispatch(startEnrollmentRequest({ personId, courseId, courseStartData: { classEnrollmentId } } as StartEnrollmentRequest));
        }
    }, [loadSuccess]);

    const getLessonStatusIsCompleted = (lesson: Lesson): boolean => {
        const hasCourseEnrollment: boolean = !!courseEnrollment;
        return hasCourseEnrollment && courseService.getLessonStatusIsCompleted(courseEnrollment.lessonEnrollments, lesson.id);
    };

    const isMyList: boolean = course?.ownerId === personId;

    const clickOptionHandler = (f: Function) => (event: SyntheticEvent) => {
        event.stopPropagation();
        event.preventDefault();
        f(event);
    };

    const changeVisibility: React.MouseEventHandler<HTMLButtonElement> = clickOptionHandler(() => {
        dispatch(
            openModal({
                id: Constants.modal.MORE_OPTIONS,
                layoutModal: <ChangeVisibilityModal />,
                isVisible: true,
                isCloseAllowed: true,
            }),
        );
    });

    const navigateToProfile = (e: SyntheticEvent, course: List) => {
        if (course.ownerId) {
            e.preventDefault();
            analyticsService.logEvent(FlowEvents.ACCESS_AUTHOR_PROFILE);
            history.push(`/profile/${course.ownerId}/info`);
        }
    };

    const getUserAvatar = () => {
        return courseTranslation?.authorAvatar ? (
            <img alt={courseTranslation.author} className="list-content__author-avatar" src={courseTranslation.authorAvatar} />
        ) : (
            <i className="material-icons-outlined">{Constants.icons.PERSON}</i>
        );
    };

    const getLinkProfileAuthor = (
        courseTranslation: List,
        children: React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>,
    ) => {
        if (courseTranslation.ownerId) {
            return (
                <a href="#" onClick={(e) => navigateToProfile(e, courseTranslation)}>
                    {children}
                </a>
            );
        }

        return children;
    };

    return (
        <>
            {loading && <Loader />}
            {!loading && courseTranslation && (
                <div className="course-page">
                    <GoBackMenu />
                    <div className="list-bg" id={Constants.TOP_IMG_ID}>
                        <img className="list-bg__img" src={courseTranslation.mainImage} />
                        <div className="container list-bg__container">
                            <div className="list-bg__corner col-sm-10"></div>
                        </div>
                    </div>
                    <div className="container">
                        <div className="row">
                            <div className="col-sm-8 offset-sm-2 list-page__adjust">
                                {isMyList && (
                                    <div className="menu-view-container">
                                        <div>
                                            <button className="button-visibility" onClick={changeVisibility} type="button">
                                                <span className="menu-view-container__list-visibility">{strings.VISIBILITY}</span>
                                                <span className="menu-view-container__container-top-list">
                                                    <span className="menu-view-container__title">
                                                        {course?.visibility ? strings.PUBLIC : strings.PRIVATE}
                                                    </span>

                                                    <i className="material-icons menu-view-container__icon-visibility">
                                                        {course?.visibility ? Constants.icons.PUBLIC : Constants.icons.LOCK}
                                                    </i>
                                                    <i className="material-icons va-m down arrow-icon">{Constants.icons.ARROW_DROP_DOWN}</i>
                                                </span>
                                            </button>
                                        </div>
                                        <div className="d-flex flex-column">
                                            <span className="menu-view-container__list-visibility">{strings.VIEWS}</span>
                                            <span className="menu-view-container__container-top-list">
                                                <i className="material-icons-outlined icon-view">{Constants.icons.VISIBILITY}</i>
                                                <span className="amount">{course.views}</span>
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <div className="list-content">
                                    <h1 className="list-content__title">{courseTranslation.name}</h1>
                                    <div className="list-content__info">
                                        <div className="list-content__author-container">
                                            {courseTranslation.ownerId && courseTranslation.author && getUserAvatar()}
                                            {getLinkProfileAuthor(
                                                courseTranslation,
                                                <span className={courseTranslation.ownerId ? 'list-content__author' : undefined}>
                                                    {courseTranslation.author}
                                                </span>,
                                            )}
                                        </div>
                                        <div className="list-content__duration">{`${course.workload || 0} ${strings.MINUTES}`}</div>
                                    </div>
                                    <HorizontalMenu
                                        entity={{ type: EntityType.COURSE, id: course.id as number, name: course.name }}
                                        hasComments
                                        hasFavorite={!isMyList}
                                        moduleId={Constants.PLAYLIST_MODULE_ID}
                                    />
                                    <p className="list-content__description">{courseTranslation.description}</p>
                                    <div className="list-content__items">
                                        {courseTranslation.lessons &&
                                            courseTranslation.lessons.map((lesson: Lesson) => (
                                                <Link
                                                    className="link-to-course"
                                                    key={lesson.id}
                                                    to={`/${Constants.COURSES_RESOURCE_NAME}/${courseId}/${Constants.LESSONS_RESOURCE_NAME}/${lesson.id}`}
                                                >
                                                    <ContentCard content={lesson.content} finished={getLessonStatusIsCompleted(lesson)} />
                                                </Link>
                                            ))}
                                    </div>
                                </div>
                            </div>

                            <SideMenu entityType={EntityType.COURSE} isMenuFixed moreOptions={isMyList ? <CourseMoreOptions /> : undefined} rating />
                        </div>
                        <div className="comments">
                            <CommentsSection entity={course} type={EntityType.COURSE} />
                        </div>
                    </div>
                    <StudentRating
                        analyticEvent={PlaylistEvents.RATE_PLAYLIST}
                        entityId={course.id as number}
                        entityName={course.name}
                        entityType={Constants.entityTypes.COURSE}
                        title={strings.LABEL_RATING_COURSE}
                    />
                </div>
            )}
        </>
    );
};

export default CoursePage;
